import {SagaIterator} from 'redux-saga';
import {isEmpty} from 'ramda';

import {takeLatest, select, put, all, call, cancel, ActionPattern} from 'redux-saga/effects';

import {sendBrowserMessage} from 'libs/analytics/sendAnalytics';
import {
    changeStep,
    PACKAGES,
    PRESCRIPTION,
    USAGE,
    StepType,
    getWizardStep,
} from 'features/wizardMs/reducers/wizardStep/wizardStep';
import {wizardScroller, isEyePrescriptionEmpty} from 'components/wizard/__utils';
import {sendPrescriptionErrorAnalytics} from '../../utils/prescriptionAnalyticsHelper/prescriptionAnalyticsHelper';
import {
    getPrescription,
    getPrescriptionValidateResult,
} from 'features/wizardMs/selectors/prescription/prescription';
import {setErrors} from 'features/wizardMs/reducers/prescription/errors/errors';
import {validatePrescription} from 'features/wizardMs/reducers/prescription/prescription/prescription';
import {getActivePackage} from 'features/wizardMs/selectors/packages/packages';
import {getActiveUsage, isFashionUsage} from 'features/wizardMs/reducers/usages/usages';
import {FILL_IT} from 'constants/wizard/prescriptionType';
import {
    isParentProductReaders,
    isSunglasses,
    getActiveProductType,
} from 'selectors/product/product';
import {getActivePower, setReadersError} from 'features/wizardMs/reducers/readers/readers';

const CONFIRM_MESSAGE = 'You have not provided a prescription. Proceed to checkout anyway?';

/**
 * Getting back to a certain step.
 * @param {string} step - Step name.
 */
export function* goTo(step: StepType) {
    const currentStep = yield select(getWizardStep);

    if (currentStep !== step) yield put(changeStep(step));
    yield call(wizardScroller, step);
}

/**
 * Validates filling of usage step and cancels task if selected just multifocal group, but not an exact usage option
 */
export function* validateUsagesStep(): SagaIterator {
    const usage = yield select(getActiveUsage);

    if (isEmpty(usage)) {
        yield call(goTo, USAGE);
        yield cancel();
    }
}

/**
 * Scrolls to the prescription step and cancels adding to cart process if the prescription filled incorrectly
 * @param {boolean} isContinueToPackageStep - for prescription step
 */
export function* validatePrescriptionStep(isContinueToPackageStep = false): any {
    const {isPrescriptionValid, errors} = yield select(getPrescriptionValidateResult);
    const [prescriptionValues, productType]: [
        ReturnType<typeof getPrescription>,
        'sunglasses' | 'eyeglasses',
    ] = yield all([select(getPrescription), select(getActiveProductType)]);

    if (isPrescriptionValid || prescriptionValues.values.method !== FILL_IT) {
        if (isContinueToPackageStep) yield call(goTo, PACKAGES);
    } else {
        yield call(sendPrescriptionErrorAnalytics, errors, productType);
        yield put(
            setErrors({
                od: {...prescriptionValues.errors.od, axi: errors.axi_od, add: errors.add_od},
                os: {...prescriptionValues.errors.os, axi: errors.axi_os, add: errors.add_os},
                pd: {...prescriptionValues.errors.pd, right: errors.pd_right, left: errors.pd_left},
            }),
        );
        yield call(goTo, PRESCRIPTION);
        yield cancel();
    }
}

/**
 * if prescription empty we show alert
 * if user cancel this alert he will go to prescription step
 */
export function* confirmEmptyPrescription() {
    const prescriptionValues = yield select(getPrescription);
    const isPrescriptionEmpty =
        isEyePrescriptionEmpty(prescriptionValues.values.od) &&
        isEyePrescriptionEmpty(prescriptionValues.values.os);

    if (isPrescriptionEmpty && prescriptionValues.values.method === FILL_IT) {
        const isConfirmed = window.confirm(CONFIRM_MESSAGE);
        yield call(sendBrowserMessage, CONFIRM_MESSAGE);
        if (!isConfirmed) {
            yield call(goTo, PRESCRIPTION);
            yield cancel();
        }
    }
}

/**
 * Scrolls to packages step and cancels adding to cart process if there is no any chosen package
 */
export function* validatePackagesStep(): SagaIterator {
    const activePackage = yield select(getActivePackage);

    if (isEmpty(activePackage)) {
        yield call(goTo, PACKAGES);
        yield cancel();
    }
}

/**
 * Scrolls to packages step and cancels adding to cart process if there is no any chosen package
 */
export function* readersValidation(): SagaIterator {
    const selectedPower = yield select(getActivePower);
    const isReaders = yield select(isParentProductReaders);
    if (isEmpty(selectedPower) && isReaders) {
        yield put(setReadersError('error'));
        yield cancel();
    }
}

/**
 * Determines if usages should be validated
 * @return {boolean} result
 */
export function* shouldValidateUsage() {
    const isPlanoGlasses = yield select(isSunglasses);
    const isReaders = yield select(isParentProductReaders);
    const isFashion = yield select(isFashionUsage);
    return !(isPlanoGlasses || isReaders || isFashion);
}

/**
 * Validates customer's configuration before adding an item to cart
 * @param {boolean} isNonRX is value for disabling validation prescription for nonRX
 */
export function* validateWizardConfiguration(isNonRX = false) {
    const isUsageValidation = yield call(shouldValidateUsage);
    // determines of product type inside readersValidation function
    yield call(readersValidation);
    if (isUsageValidation) {
        yield call(validateUsagesStep);
        if (!isNonRX) yield call(validatePrescriptionStep);
        yield call(validatePackagesStep);
        yield call(confirmEmptyPrescription);
    }
}

/**
 * Root
 */
export function* wizardValidation(): SagaIterator {
    yield takeLatest<ActionPattern, (...args: any[]) => any>(
        `${validatePrescription}`,
        validatePrescriptionStep,
        true,
    );
}
